import React, { useMemo } from 'react';
import { Card, Typography } from 'antd';
import { Column, Line } from '@ant-design/charts';
import type { PvpLiveDto } from '@/types/pvp';
import {
  ONLINE_SERIES,
  completionRows,
  doneRows,
  fmtNum,
  fmtPct,
  onlineRows,
  timeAxis,
  tooltipTitle,
} from './model';

const { Text } = Typography;

const NO_ANIMATION = { animate: false };
const LEGEND = { color: { position: 'top' as const } };
const ONLINE_COLORS = ['#1677ff', '#13c2c2', '#722ed1'];

/** Players online, live matches and players waiting; matches done with completion % under it. */
export const LiveCharts: React.FC<{ dto: PvpLiveDto }> = ({ dto }) => {
  const { range, bucketMin } = dto;
  const online = useMemo(() => onlineRows(dto), [dto]);
  const done = useMemo(() => doneRows(dto), [dto]);
  const completion = useMemo(() => completionRows(dto), [dto]);
  const title = tooltipTitle(range);

  return (
    <>
      <Card size="small" title="Players online, live matches and players waiting">
        <Line
          data={online}
          xField="t"
          yField="value"
          colorField="series"
          height={260}
          legend={LEGEND}
          scale={{ color: { domain: ONLINE_SERIES, range: ONLINE_COLORS } }}
          axis={timeAxis(range, (v) => fmtNum(v, 0))}
          tooltip={{
            title,
            items: [{ channel: 'y', valueFormatter: (v: number) => fmtNum(v, 0) }],
          }}
          {...NO_ANIMATION}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          The peak of each {fmtNum(bucketMin, 0)}-minute bucket. Times are IST.
        </Text>
      </Card>

      <Card size="small" title="Matches done" style={{ marginTop: 12 }}>
        <Column
          data={done}
          xField="t"
          yField="done"
          height={180}
          axis={timeAxis(range, (v) => fmtNum(v, 0))}
          tooltip={{
            title,
            items: [
              { channel: 'y', name: 'Matches done', valueFormatter: (v: number) => fmtNum(v, 0) },
            ],
          }}
          {...NO_ANIMATION}
        />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Completion % (completed or drawn, of matches done)
        </Text>
        <Line
          data={completion}
          xField="t"
          yField="value"
          height={120}
          scale={{ y: { domain: [0, 100] } }}
          axis={timeAxis(range, (v) => `${fmtNum(v, 0)}%`)}
          tooltip={{
            title,
            items: [{ channel: 'y', name: 'Completion', valueFormatter: (v: number) => fmtPct(v) }],
          }}
          {...NO_ANIMATION}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          A gap in the line is a bucket in which no match ended.
        </Text>
      </Card>
    </>
  );
};
